import { SettingOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Descriptions, Divider, Dropdown } from 'antd';
import clsx from 'clsx';
import { groupBy, includes, map, sortBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { toast } from '../../../../../../../../apollo';
import { AppContext } from '../../../../../../../../AppContext';
import {
  AddButton,
  DeleteButton,
  DragIcon,
  DuplicateIcon,
  EditButton,
  KebabMenu,
  PersonDone,
} from '../../../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../../../common/CommonUseLazyQuery';
import {
  ACCESS_TYPE,
  ADDONS,
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  APPROVER_MODE,
  BREAKPOINTS,
  BULK_ACTION,
  BULK_JOB_STATUS,
  DEFAULT_PAGE_SIZE,
  DESELECT_MESSAGE,
  EQC_STAGE_CONFIG_LABEL,
  GA_EVENT,
  GA_LABEL,
  PROJECT_CHECKLIST_STATUS,
  REQUEST_FEATURE_UPGRADE_KEYS,
} from '../../../../../../../../common/constants';
import { Event } from '../../../../../../../../common/trackEvents';
import useRouter from '../../../../../../../../common/useRouter';
import CanPerform from '../../../../../../../../components/CanPerform';
import CommonBulkDeleteModal from '../../../../../../../../components/CommonBulkDeleteModal';
import CommonPopover from '../../../../../../../../components/CommonPopover';
import CommonSelectionBar from '../../../../../../../../components/CommonSelectionBar';
import CommonTable from '../../../../../../../../components/CommonTable';
import CommonTooltip from '../../../../../../../../components/CommonTooltip';
import CustomCheckboxSelection from '../../../../../../../../components/CustomCheckboxSelection';
import '../../../../../../../../components/CustomCheckboxSelection.less';
import EllipsisText from '../../../../../../../../components/EllipsisText';
import Portal from '../../../../../../../../components/Portal';
import UpgradeModal from '../../../../../../../../components/UpgradeModal';
import {
  BULK_DEACTIVATE_PROJECT_EQC_TYPE_STAGES,
  DELETE_PROJECT_STAGE,
  REORDER_PROJECT_STAGE,
  UPDATE_PROJECT_EQC_STAGE,
} from '../../../../../../graphql/Mutation';
import {
  GET_BULK_PROJECT_STAGE_JOB_STATUS,
  GET_PROJECT_STAGE_LIST,
  GET_STAGE_LEVEL_WISE_USERS,
} from '../../../../../../graphql/Queries';
import ApproversDrawer from '../../ApproversDrawer';
import DuplicateEqcTypeModal from '../../DuplicateEqcType';
import DeleteModalStage from '../DeleteModalStage';
import AddEditStageModal from './AddEditStageModal';
import AddEmailModal from './AddEmailModal';
import BulkAssignApproversDrawer from './BulkAssignApproversDrawer';
import BulkEditSettingsDrawer from './BulkEditSettingsDrawer';
import BulkRemoveApproversDrawer from './BulkRemoveApproversDrawer';

const RowContext = React.createContext({});
const Rows = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    // eslint-disable-next-line react/destructuring-assignment
    id: props['data-row-key'], // ----> getting id
  });
  const style = {
    // eslint-disable-next-line react/destructuring-assignment
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners],
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const DragHandle = ({ disabled }) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <DragIcon
      className={clsx('cursor-move', disabled && 'cursor-not-allowed')}
      ref={setActivatorNodeRef}
      {...(!disabled && listeners)}
    />
  );
};

const StageListTable = ({
  setStageId,
  setTitle,
  stageId,
  eqcTypeData,
  isSequenceChanged,
  setIsSequenceChanged,
  setStageData: setSelectedStageData,
  isEditable,
  refetchRef,
  isStageItemSelectionActive,
  setIsStageSelectionActive,
}) => {
  const {
    params: { eqcTypeId, projectId },
  } = useRouter();
  const initialStageFilter = {
    skip: 0,
    limit: 10,
    projectEqcTypeId: Number(eqcTypeId),
    sortBy: { field: 'order', order: 'ASC' },
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };
  const { getCurrentUser, getTenantUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const currentTenant = getTenantUser();
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [showModal, setShowModal] = useState(false);
  const [deleteStageModal, setDeleteStageModal] = useState(false);
  const [stageData, setStageData] = useState();
  const [selectedData, setSelectedData] = useState();
  const [dataSource, setDataSource] = useState([]);
  const [stageFilter, setStageFilter] = useState(initialStageFilter);
  const [open, setOpen] = useState(false);
  const [drawerData, setDrawerData] = useState();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureKey, setFeatureKey] = useState(
    REQUEST_FEATURE_UPGRADE_KEYS.STAGE_MAX_LIMIT,
  );
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [activeStageCount, setActiveStageCount] = useState(0);
  const [rfiStageCount, setRfiStageCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllAcrossPages, setIsSelectAllAcrossPages] = useState(false);
  const [exceptIds, setExceptIds] = useState([]);
  const [showBulkEditSettingsDrawer, setShowBulkEditSettingsDrawer] =
    useState(false);
  const [showBulkAssignApproversDrawer, setShowBulkAssignApproversDrawer] =
    useState(false);
  const [showBulkRemoveApproversDrawer, setShowBulkRemoveApproversDrawer] =
    useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [approversWithoutEmail, setApproversWithoutEmail] = useState([]);
  const bulkJobsRef = useRef(new Map());
  const stageFilterRef = useRef(stageFilter);
  stageFilterRef.current = stageFilter;

  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const isAnyRowSelected =
    (selectedRows?.length ?? 0) > 0 || isSelectAllAcrossPages;

  const [prefetchedStageData, setPrefetchedStageData] = useState(null);
  const [getStageLevelWiseUsers, { loading: checkingRemoveApprovers }] =
    useLazyQueryWithCallbacks(GET_STAGE_LEVEL_WISE_USERS, {
      fetchPolicy: 'network-only',
      onCompleted: ({ stageLevelWiseUsers }) => {
        const data = stageLevelWiseUsers?.data || [];
        const hasApprovers = data?.some(
          (stage) =>
            stage?.levels?.some(
              (level) =>
                level?.internalUsers?.length > 0 ||
                level?.externalUsers?.length > 0,
            ) ||
            stage?.rfiLevels?.some(
              (level) =>
                level?.internalUsers?.length > 0 ||
                level?.externalUsers?.length > 0,
            ),
        );
        if (hasApprovers) {
          setPrefetchedStageData(data);
          setShowBulkRemoveApproversDrawer(true);
        } else {
          toast({
            message: 'No approvers assigned for the selected stages',
            type: 'warning',
          });
        }
      },
      onError() {},
    });

  const [fetchStageData, { loading }] = useLazyQueryWithCallbacks(
    GET_PROJECT_STAGE_LIST,
    {
      variables: { filter: stageFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const stageList = res?.projectEqcTypeStageList?.data || [];
        setSelectedStageData(res?.projectEqcTypeStageList?.data);
        setActiveStageCount(res?.projectEqcTypeStageList?.activeStageCount);
        setRfiStageCount(res?.projectEqcTypeStageList?.rfiStageCount || 0);
        const matchedStage = stageId
          ? stageList?.find((stage) => stage?.id === stageId)
          : null;
        const activeStage = matchedStage || stageList?.[0];
        if (activeStage) {
          setStageId(activeStage?.id);
          setTitle(activeStage?.name);
        }
        const pagination = {
          ...paginationProp,
          total: res?.projectEqcTypeStageList?.total,
        };
        setPaginationProp(pagination);
        setDataSource(sortBy(stageList, ['order']));
        if (isSequenceChanged) {
          setStageId(stageId);
          setIsSequenceChanged(false);
        }

        if (isSelectAllAcrossPages) {
          const existingIds = selectedRows?.map((row) => row?.id) || [];
          const newItemsToAdd =
            stageList?.filter(
              (item) =>
                item?.isActive &&
                !existingIds?.includes(item?.id) &&
                !exceptIds?.includes(item?.id),
            ) || [];
          const updatedSelectedRows = [...selectedRows, ...newItemsToAdd];
          const updatedSelectedKeys =
            updatedSelectedRows
              ?.map((row) => row?.id)
              ?.filter((id) => !exceptIds?.includes(id)) || [];
          setSelectedRows(updatedSelectedRows);
          setSelectedRowKeys(updatedSelectedKeys);
        }
      },
      onError() {},
    },
  );

  useEffect(() => {
    if (isSequenceChanged) {
      fetchStageData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSequenceChanged]);

  const handleRefetchAfterDelete = (deletedCount = 1) => {
    const currentTotal = paginationProp?.total || 0;
    const pageSize = paginationProp?.pageSize || DEFAULT_PAGE_SIZE;
    const newTotal = Math.max(0, currentTotal - deletedCount);
    const maxPage = Math.max(1, Math.ceil(newTotal / pageSize));
    const newCurrent = Math.min(paginationProp?.current || 1, maxPage);
    const newSkip = (newCurrent - 1) * pageSize;

    setStageFilter({
      ...stageFilter,
      skip: newSkip,
    });
    setPaginationProp({
      ...paginationProp,
      current: newCurrent,
    });
    setSelectedStageData();
    fetchStageData({
      variables: {
        filter: {
          ...stageFilter,
          skip: newSkip,
        },
      },
    });
  };

  const [updateProjectEqcTypeStage] = useMutation(UPDATE_PROJECT_EQC_STAGE, {
    onError() {},
    onCompleted() {
      Event(GA_EVENT.DELETE_PROJECT_CHECKLIST_STAGE, {
        label: GA_LABEL.DELETE_PROJECT_CHECKLIST_STAGE,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        project_id: projectId,
        project_checklist_id: eqcTypeId,
        project_stage_id: selectedData?.id,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setDeleteStageModal(false);
      handleRefetchAfterDelete();
    },
  });

  const [deleteStage] = useMutation(DELETE_PROJECT_STAGE, {
    onError() {},
    onCompleted() {
      Event(GA_EVENT.DELETE_PROJECT_CHECKLIST_STAGE, {
        label: GA_LABEL.DELETE_PROJECT_CHECKLIST_STAGE,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        project_id: projectId,
        project_checklist_id: eqcTypeId,
        project_stage_id: selectedData?.id,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setDeleteStageModal(false);
      handleRefetchAfterDelete();
    },
  });

  useEffect(() => {
    fetchStageData({ variables: { filter: stageFilter } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchStageDetails = (navigateToLastPage = false) => {
    if (navigateToLastPage) {
      const newTotal = (paginationProp?.total || 0) + 1;
      const pageSize = paginationProp?.pageSize || DEFAULT_PAGE_SIZE;
      const lastPage = Math.ceil(newTotal / pageSize);
      const lastPageSkip = (lastPage - 1) * pageSize;

      setStageFilter((prev) => ({ ...prev, skip: lastPageSkip }));
      setPaginationProp((prev) => ({
        ...prev,
        total: newTotal,
        current: lastPage,
      }));
      fetchStageData({
        variables: { filter: { ...stageFilter, skip: lastPageSkip } },
      });
      return;
    }
    fetchStageData({
      variables: { filter: stageFilter },
    });
  };

  useEffect(() => {
    if (refetchRef?.current) {
      // eslint-disable-next-line no-param-reassign
      refetchRef.current.refetchStageDetails = refetchStageDetails;
    }
  }, [stageFilter, paginationProp]);

  const [reorderStage, { loading: reorderLoading }] = useMutation(
    REORDER_PROJECT_STAGE,
    {
      onCompleted() {
        Event(GA_EVENT.REORDER_PROJECT_CHECKLIST_STAGE, {
          label: GA_LABEL.REORDER_PROJECT_CHECKLIST_STAGE,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          project_id: projectId,
          project_checklist_id: eqcTypeId,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
      },
      onError() {},
    },
  );

  const startJobTracking = useCallback((jobId, action) => {
    if (!jobId || !action) return;
    bulkJobsRef.current?.set(jobId, { action, timeoutId: null });
  }, []);

  const updateJobTimeout = useCallback((jobId, timeoutId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo) {
      bulkJobsRef.current?.set(jobId, { ...jobInfo, timeoutId });
    }
  }, []);

  const clearJobTracking = useCallback((jobId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo?.timeoutId) {
      clearTimeout(jobInfo?.timeoutId);
    }
    bulkJobsRef.current?.delete(jobId);
  }, []);

  const getJobAction = useCallback((jobId) => {
    if (!jobId) return null;
    return bulkJobsRef.current?.get(jobId)?.action || null;
  }, []);

  const handleClearBulkSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
  }, []);

  const [getBulkProjectStageJobStatus] = useLazyQueryWithCallbacks(
    GET_BULK_PROJECT_STAGE_JOB_STATUS,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        const { state, jobId } = res?.getBulkProjectStageJobStatus || {};

        if (!jobId) return;

        const currentAction = getJobAction(jobId);

        if (state === BULK_JOB_STATUS.PROCESSING) {
          const timeoutId = setTimeout(() => {
            getBulkProjectStageJobStatus({
              variables: { jobId },
            });
          }, 3000);
          updateJobTimeout(jobId, timeoutId);
        }

        if (
          state === BULK_JOB_STATUS.COMPLETED ||
          state === BULK_JOB_STATUS.FAILED
        ) {
          clearJobTracking(jobId);
          if (state === BULK_JOB_STATUS.COMPLETED) {
            if (currentAction === BULK_ACTION.DELETE) {
              handleRefetchAfterDelete(selectedRows?.length);
            } else {
              fetchStageData({ variables: { filter: stageFilter } });
            }
            // Check for approvers without email in job result
            const jobResult = res?.getBulkProjectStageJobStatus?.result;
            if (jobResult) {
              try {
                const parsed =
                  typeof jobResult === 'string'
                    ? JSON.parse(jobResult)
                    : jobResult;
                if (parsed?.approversWithoutEmail?.length > 0) {
                  setApproversWithoutEmail(parsed?.approversWithoutEmail);
                  setShowEmailModal(true);
                }
              } catch {
                // result is not parseable, ignore
              }
            }
          }
          handleClearBulkSelection();
        }
      },
    },
  );

  const [bulkDeactivateStages, { loading: bulkDeleteLoading }] = useMutation(
    BULK_DEACTIVATE_PROJECT_EQC_TYPE_STAGES,
    {
      onCompleted(response) {
        const { status, jobId } =
          response?.bulkDeactivateProjectEqcTypeStage || {};

        if (status === BULK_JOB_STATUS.SUCCESS && jobId) {
          startJobTracking(jobId, BULK_ACTION.DELETE);
          getBulkProjectStageJobStatus({
            variables: { jobId },
          });
        }
        setShowBulkDeleteModal(false);
      },
      onError() {},
    },
  );

  const handleAddEditUser = (record) => {
    if (record) {
      const data = {
        ...record,
        pass: includes(
          record?.stageConfiguration,
          EQC_STAGE_CONFIG_LABEL.BYPASS,
        ),
        skip: includes(record?.stageConfiguration, EQC_STAGE_CONFIG_LABEL.SKIP),
      };
      setStageData(data);
    } else {
      setStageData();
    }
    setShowModal(true);
  };

  const handleTableChange = (pagination) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setStageFilter({ ...stageFilter, skip, limit: pagination.pageSize });
    setPaginationProp({ ...paginationProp, ...pagination });

    if (!isSelectAllAcrossPages) {
      setSelectedRowKeys([]);
    }

    fetchStageData({
      variables: {
        filter: {
          ...stageFilter,
          skip,
          limit: pagination.pageSize,
        },
      },
    });
  };

  const handleEdit = (e, record) => {
    e?.stopPropagation?.();
    handleAddEditUser(record);
  };
  const handleDuplicate = (e, record) => {
    e?.stopPropagation?.();
    setShowDuplicateModal(true);
    setSelectedData(record);
  };

  const handleActiveAndDelete = (record) => {
    if (
      !record?.isActive &&
      activeStageCount === currentTenant?.tenant?.featureConfig?.stageMaxLimit
    ) {
      setFeatureKey(REQUEST_FEATURE_UPGRADE_KEYS.STAGE_MAX_LIMIT);
      setShowUpgradeModal(true);
    } else {
      setDeleteStageModal(true);
      setSelectedData(record);
    }
  };

  const getLevelWiseApprovers = (record) => {
    const levelWiseApprovers = groupBy(record, (b) => {
      return b?.levelNumber;
    });
    return map(Object.keys(levelWiseApprovers), (data) => {
      return (
        <Descriptions.Item
          label={`${levelWiseApprovers?.[data]?.[0]?.levelName}:`}
          span={3}
        >
          {map(levelWiseApprovers?.[data], (approver) => {
            return (
              approver?.externalUserApprover?.name ||
              approver?.internalApprover?.user?.name
            );
          }).join(', ')}
        </Descriptions.Item>
      );
    });
  };

  const getApprovers = (record) => {
    const { projectEqcTypeStageApprovals } = record;

    return (
      <div>
        <div className="d-flex align-center">
          {projectEqcTypeStageApprovals?.length > 0 &&
            (isDesktopViewport ? (
              <CommonPopover
                classNames={{
                  root: 'checklist-approvers-popup',
                  container: 'checklist-approvers-container',
                }}
                getPopupContainer={() =>
                  // eslint-disable-next-line no-undef
                  document.querySelector('.eqc-type-details')
                }
                title="Approvers info:"
                trigger={isDesktopViewport ? 'hover' : 'click'}
                placement={isDesktopViewport ? 'right' : 'top'}
                destroyOnHidden
                content={
                  <div onClick={(e) => e.stopPropagation()}>
                    <Descriptions
                      column={3}
                      layout="vertical"
                      colon={false}
                      className="inner"
                    >
                      {getLevelWiseApprovers(projectEqcTypeStageApprovals)}
                    </Descriptions>
                  </div>
                }
              >
                {projectEqcTypeStageApprovals?.length > 0 && (
                  <PersonDone
                    className="checklist-approvers mr-10"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                )}
              </CommonPopover>
            ) : (
              <>
                {projectEqcTypeStageApprovals?.length > 0 && (
                  <PersonDone
                    className="checklist-approvers mr-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDrawerData(projectEqcTypeStageApprovals);
                      setOpen(true);
                    }}
                  />
                )}
              </>
            ))}
        </div>
      </div>
    );
  };

  const handleRowSelectionChange = (selectedKeys, selectedRowsData) => {
    if (isSelectAllAcrossPages) {
      const currentPageKeys = dataSource?.map((item) => item?.id) || [];
      const newlyDeselected =
        currentPageKeys?.filter((key) => !selectedKeys?.includes(key)) || [];
      const reSelected =
        selectedKeys?.filter((key) => exceptIds?.includes(key)) || [];
      setExceptIds((prev) => {
        let updated = [...prev];
        newlyDeselected?.forEach((id) => {
          if (!updated?.includes(id)) updated.push(id);
        });
        updated = updated?.filter((id) => !reSelected?.includes(id));
        return updated;
      });
      setSelectedRowKeys(selectedKeys);
      return;
    }
    const currentPageKeys = dataSource?.map((item) => item?.id) || [];
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];
    const activeSelectedRowsData =
      selectedRowsData?.filter((row) => row?.isActive) || [];
    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...activeSelectedRowsData,
    ];
    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];
    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectRow = (record, selected) => {
    if (!record?.isActive) return;
    const recordId = record?.id;

    if (selected) {
      if (isSelectAllAcrossPages) {
        setExceptIds((prev) => prev?.filter((id) => id !== recordId) || []);
        setSelectedRowKeys((prev) => [...prev, recordId]);
        return;
      }
      const isAlreadySelected = selectedRows?.some(
        (row) => row?.id === recordId,
      );
      const newSelectedRows = isAlreadySelected
        ? selectedRows
        : [...selectedRows, record];
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];
      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
    } else {
      if (isSelectAllAcrossPages) {
        setExceptIds((prev) => [...prev, recordId]);
        const remainingKeys =
          selectedRowKeys?.filter((key) => key !== recordId) || [];
        setSelectedRowKeys(remainingKeys);
        return;
      }
      const newSelectedRows = selectedRows?.filter(
        (row) => row?.id !== recordId,
      );
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];
      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      const activeItems = dataSource?.filter((item) => item?.isActive) || [];
      if (isSelectAllAcrossPages) {
        const activePageKeys = activeItems?.map((item) => item?.id) || [];
        setExceptIds(
          (prev) => prev?.filter((id) => !activePageKeys?.includes(id)) || [],
        );
        setSelectedRowKeys(activePageKeys);
        return;
      }
      const allKeys = activeItems?.map((item) => item?.id) || [];
      setSelectedRowKeys(allKeys);
      setSelectedRows(activeItems);
    } else {
      if (isSelectAllAcrossPages) {
        const currentPageKeys = dataSource?.map((item) => item?.id) || [];
        setExceptIds((prev) => {
          const updated = [...prev];
          currentPageKeys?.forEach((id) => {
            if (!updated?.includes(id)) updated.push(id);
          });
          return updated;
        });
        setSelectedRowKeys([]);
        return;
      }
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  };

  const handleSelectAllOnPage = () => {
    const activeItems = dataSource?.filter((item) => item?.isActive) || [];
    const activePageKeys = activeItems?.map((item) => item?.id) || [];
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !activePageKeys?.includes(row?.id)) || [];
    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...activeItems,
    ];
    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];
    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectAllAcrossPages = async () => {
    try {
      setExceptIds([]);
      setIsSelectAllAcrossPages(true);
      const activeItems = dataSource?.filter((item) => item?.isActive) || [];
      const activePageKeys = activeItems?.map((item) => item?.id) || [];
      setSelectedRowKeys(activePageKeys);
      setSelectedRows(activeItems);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleBulkDelete = () => {
    if (isAnyRowSelected) {
      setShowBulkDeleteModal(true);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (isAnyRowSelected) {
      try {
        const mutationData = {
          isAllSelected: isSelectAllAcrossPages,
          projectEqcTypeId: Number(eqcTypeId),
        };

        if (!isSelectAllAcrossPages) {
          mutationData.projectEqcTypeStageIds =
            selectedRows?.map((row) => row?.id) || [];
        }
        if (isSelectAllAcrossPages && exceptIds?.length > 0) {
          mutationData.exceptProjectEqcTypeStageIds = exceptIds;
        }

        await bulkDeactivateStages({
          variables: {
            data: mutationData,
          },
        });
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  };

  // Restore selections when data changes
  useEffect(() => {
    if (selectedRows?.length > 0 && dataSource?.length > 0) {
      const currentPageIds = dataSource?.map((item) => item?.id) || [];
      const selectedIdsOnCurrentPage =
        selectedRows
          ?.filter(
            (row) =>
              currentPageIds?.includes(row?.id) &&
              !(isSelectAllAcrossPages && exceptIds?.includes(row?.id)),
          )
          ?.map((row) => row?.id) || [];
      setSelectedRowKeys(selectedIdsOnCurrentPage);
    } else if (selectedRows?.length === 0 && dataSource?.length > 0) {
      setSelectedRowKeys([]);
    }
  }, [dataSource, selectedRows]);

  // Auto-clear selection when all items have been excluded
  useEffect(() => {
    if (
      isSelectAllAcrossPages &&
      exceptIds?.length >= (paginationProp?.total || 0) &&
      paginationProp?.total > 0
    ) {
      handleClearBulkSelection();
    }
  }, [
    exceptIds,
    isSelectAllAcrossPages,
    paginationProp?.total,
    handleClearBulkSelection,
  ]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      bulkJobsRef?.current?.forEach((jobInfo) => {
        if (jobInfo?.timeoutId) {
          clearTimeout(jobInfo?.timeoutId);
        }
      });
      bulkJobsRef.current?.clear();
    };
  }, []);

  // Notify parent when selection changes
  useEffect(() => {
    setIsStageSelectionActive?.(isAnyRowSelected);
  }, [isAnyRowSelected, setIsStageSelectionActive]);

  const getRowSelection = () => {
    if (!isDesktopViewport) {
      return undefined;
    }

    if (!isEditable || !eqcTypeData?.project?.isActive) {
      return undefined;
    }

    return {
      selectedRowKeys,
      onChange: handleRowSelectionChange,
      onSelect: handleSelectRow,
      onSelectAll: handleSelectAll,
      getCheckboxProps: (record) => ({
        disabled: !record?.isActive,
        name: record?.name,
      }),
      renderCell: (checked, record, index, originNode) => {
        if (!record?.isActive) {
          return (
            <CommonTooltip
              title="You cannot select the deactivated stages"
              getPopupContainer={() =>
                // eslint-disable-next-line no-undef
                document.getElementById('stage-list-container') ||
                // eslint-disable-next-line no-undef
                document.body
              }
            >
              {originNode}
            </CommonTooltip>
          );
        }
        return originNode;
      },
      columnTitle: (
        <CustomCheckboxSelection
          checked={
            selectedRowKeys?.length === dataSource?.length &&
            dataSource?.length > 0
          }
          indeterminate={
            selectedRowKeys?.length > 0 &&
            selectedRowKeys?.length < dataSource?.length
          }
          disabled={!dataSource?.length}
          onSelectAllPage={handleSelectAllOnPage}
          onSelectAllAcross={handleSelectAllAcrossPages}
          onDeselectAll={handleClearBulkSelection}
          currentPageCount={dataSource?.length}
          totalCount={paginationProp?.total}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          selectedCount={
            isSelectAllAcrossPages
              ? (activeStageCount || 0) - (exceptIds?.length || 0)
              : selectedRowKeys?.length || 0
          }
        />
      ),
    };
  };

  const getAddButtonTooltip = () => {
    if (isStageItemSelectionActive || isAnyRowSelected) return DESELECT_MESSAGE;
    return '';
  };
  const addButtonTooltip = getAddButtonTooltip();

  const columns = [
    ...(isEditable
      ? [
          {
            width: '5%',
            align: 'center',
            className: 'drag-visible',
            render: (record) =>
              eqcTypeData?.project?.isActive &&
              isEditable &&
              record?.type === ACCESS_TYPE.INSPECTION && (
                <CommonTooltip title="Change order">
                  <div className="p-16">
                    <DragHandle disabled={isAnyRowSelected} />
                  </div>
                </CommonTooltip>
              ),
          },
        ]
      : []),
    {
      title: 'NAME',
      dataIndex: 'name',
      className: 'drag-visible',
      key: 'name',
      render: (text, record) => (
        <>
          {record?.rfiStage && (
            <>
              <div
                className={clsx(
                  'stage-wrapper',
                  record?.rfiStage?.id === stageId && 'fw-semi-bold',
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isAnyRowSelected) return;
                  setTitle(record?.rfiStage?.name);
                  setStageId(record?.rfiStage?.id);
                }}
              >
                <div className="title">
                  <EllipsisText text={record?.rfiStage?.name} />
                </div>
                <div className="d-flex justify-end">
                  <>{getApprovers(record?.rfiStage)}</>
                </div>
              </div>
              <Divider />
            </>
          )}
          <div
            className={clsx(
              'stage-wrapper',
              record?.id === stageId && 'fw-semi-bold',
            )}
          >
            <div className="title">
              <EllipsisText text={text} />
            </div>
            <div className="d-flex justify-end">
              <div className="d-flex action-icons">
                <>{getApprovers(record)}</>
                {record?.type === ACCESS_TYPE.INSPECTION && isEditable && (
                  <>
                    {!includes(
                      [
                        PROJECT_CHECKLIST_STATUS.PUBLISHED,
                        PROJECT_CHECKLIST_STATUS.ARCHIVED,
                      ],
                      eqcTypeData?.projectEqcType?.status,
                    ) && (
                      <CommonTooltip
                        trigger="hover"
                        title={
                          isAnyRowSelected || isStageItemSelectionActive
                            ? DESELECT_MESSAGE
                            : 'Edit'
                        }
                      >
                        <Button
                          shape="circle"
                          icon={<EditButton />}
                          className="edit-button pointer b-0"
                          disabled={
                            eqcTypeData?.status ===
                              PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                            !eqcTypeData?.project?.isActive ||
                            !record?.isActive ||
                            isAnyRowSelected ||
                            isStageItemSelectionActive
                          }
                          onClick={(e) => handleEdit(e, record)}
                        />
                      </CommonTooltip>
                    )}
                    <CommonTooltip
                      trigger="hover"
                      title={
                        isAnyRowSelected || isStageItemSelectionActive
                          ? DESELECT_MESSAGE
                          : 'Duplicate'
                      }
                    >
                      <Button
                        shape="circle"
                        icon={<DuplicateIcon />}
                        disabled={
                          !record?.isActive ||
                          eqcTypeData?.status ===
                            PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                          !eqcTypeData?.project?.isActive ||
                          isAnyRowSelected ||
                          isStageItemSelectionActive
                        }
                        onClick={(e) => {
                          if (
                            activeStageCount >=
                            currentTenant?.tenant?.featureConfig?.stageMaxLimit
                          ) {
                            e?.stopPropagation?.();
                            setFeatureKey(
                              REQUEST_FEATURE_UPGRADE_KEYS.STAGE_MAX_LIMIT,
                            );
                            setShowUpgradeModal(true);
                          } else {
                            handleDuplicate(e, record);
                          }
                        }}
                      />
                    </CommonTooltip>
                    {!includes(
                      [
                        PROJECT_CHECKLIST_STATUS.PUBLISHED,
                        PROJECT_CHECKLIST_STATUS.ARCHIVED,
                      ],
                      eqcTypeData?.projectEqcType?.status,
                    ) && (
                      <CommonTooltip
                        trigger="hover"
                        title={
                          isAnyRowSelected || isStageItemSelectionActive
                            ? DESELECT_MESSAGE
                            : 'Delete'
                        }
                      >
                        <Button
                          className={clsx(
                            'delete-button pointer b-0',
                            (eqcTypeData?.status ===
                              PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
                              eqcTypeData?.status ===
                                PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                              !eqcTypeData?.project?.isActive ||
                              isAnyRowSelected ||
                              isStageItemSelectionActive) &&
                              'disabled-button',
                          )}
                          shape="circle"
                          icon={<DeleteButton />}
                          disabled={
                            eqcTypeData?.status ===
                              PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
                            eqcTypeData?.status ===
                              PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                            !eqcTypeData?.project?.isActive ||
                            isAnyRowSelected ||
                            isStageItemSelectionActive
                          }
                          onClick={(e) => {
                            e?.stopPropagation?.();
                            handleActiveAndDelete(record);
                          }}
                        />
                      </CommonTooltip>
                    )}
                    {includes(
                      [
                        PROJECT_CHECKLIST_STATUS.PUBLISHED,
                        PROJECT_CHECKLIST_STATUS.ARCHIVED,
                      ],
                      eqcTypeData?.projectEqcType?.status,
                    ) && (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: record?.isActive ? 'deactivate' : 'activate',
                              label: (
                                <span
                                  onClick={() => handleActiveAndDelete(record)}
                                >
                                  {record?.isActive ? 'Deactivate' : 'Activate'}
                                </span>
                              ),
                            },
                          ],
                        }}
                        trigger={['click']}
                        getPopupContainer={(triggerNode) =>
                          triggerNode?.parentNode
                        }
                      >
                        <CommonTooltip
                          trigger="hover"
                          title={
                            isAnyRowSelected || isStageItemSelectionActive
                              ? DESELECT_MESSAGE
                              : ''
                          }
                        >
                          <Button
                            shape="circle"
                            className="kebab-icon m-0"
                            icon={<KebabMenu />}
                            disabled={
                              isAnyRowSelected || isStageItemSelectionActive
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </CommonTooltip>
                      </Dropdown>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  const DraggableBodyRow = async ({ over, active }) => {
    if (!active || !over || active?.id === over?.id) return;
    if (isAnyRowSelected) return;
    const overIndex = over?.data?.current?.sortable?.index;
    const activeIndex = active?.data?.current?.sortable?.index;
    const data = [...dataSource];
    setDataSource(arrayMove(data, activeIndex, overIndex));
    try {
      const { errors } = await reorderStage({
        variables: {
          data: {
            stageIds: [active?.id, over?.id],
            projectEqcTypeId: Number(eqcTypeId),
          },
        },
      });
      if (errors) {
        throw errors;
      }
    } catch (error) {
      setDataSource(data);
    }
  };

  const getRowClassName = (record) => {
    let rowClassName = 'stage-row';
    if (
      (record?.id === stageId || record?.rfiStage?.id === stageId) &&
      record?.isActive
    )
      rowClassName += ' row-orange';
    if (!record?.isActive) rowClassName += ' row-disabled';
    if (isAnyRowSelected) rowClassName += ' cursor-not-allowed';
    return rowClassName;
  };

  return (
    <div id="stage-list-container">
      <CanPerform
        action={ALLOWED_ACTION_KEYS.ADD_PROJECT_STAGE}
        type={ALLOWED_ACTION_TYPE.BOTH}
      >
        {showModal && (
          <AddEditStageModal
            showModal={showModal}
            setShowModal={setShowModal}
            stageData={stageData}
            isUpdate={!!stageData}
            setStageData={setStageData}
            refetchStageDataWithInitialValues={refetchStageDetails}
            eqcTypeId={eqcTypeId}
            eqcTypeData={eqcTypeData}
            isDisabled={
              // eqcTypeData?.status === PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
              !eqcTypeData?.project?.isActive
            }
          />
        )}
        {showDuplicateModal && (
          <DuplicateEqcTypeModal
            showModal={showDuplicateModal}
            setShowModal={setShowDuplicateModal}
            refetchEqcTypeData={refetchStageDetails}
            projectEqcTypeStageId={selectedData?.id}
            title="Duplicate Checklist Stage"
          />
        )}
        {deleteStageModal && (
          <DeleteModalStage
            showModal={deleteStageModal}
            setShowModal={setDeleteStageModal}
            data={selectedData}
            mutation={
              !includes(
                [
                  PROJECT_CHECKLIST_STATUS.PUBLISHED,
                  PROJECT_CHECKLIST_STATUS.ARCHIVED,
                ],
                eqcTypeData?.projectEqcType?.status,
              )
                ? deleteStage
                : updateProjectEqcTypeStage
            }
            eqcTypeId={eqcTypeId}
            isDeletable={
              !includes(
                [
                  PROJECT_CHECKLIST_STATUS.PUBLISHED,
                  PROJECT_CHECKLIST_STATUS.ARCHIVED,
                ],
                eqcTypeData?.projectEqcType?.status,
              )
            }
          />
        )}
      </CanPerform>
      {isDesktopViewport && isAnyRowSelected && (
        <Portal portalId="stage-selection-bar">
          <CommonSelectionBar
            selectedCount={
              isSelectAllAcrossPages
                ? (activeStageCount || 0) - (exceptIds?.length || 0)
                : selectedRows?.length || 0
            }
            onClear={handleClearBulkSelection}
          >
            <Dropdown
              menu={{
                items: [
                  {
                    key: APPROVER_MODE.ASSIGN?.key,
                    label: APPROVER_MODE.ASSIGN?.label,
                    onClick: () => {
                      if (
                        !includes(
                          currentTenant?.tenant?.addOn,
                          ADDONS?.MULTI_LEVEL_APPROVAL,
                        )
                      ) {
                        setFeatureKey(
                          REQUEST_FEATURE_UPGRADE_KEYS.STAGE_APPROVAL,
                        );
                        setShowUpgradeModal(true);
                      } else {
                        setShowBulkAssignApproversDrawer(true);
                      }
                    },
                  },
                  {
                    key: APPROVER_MODE.REMOVE?.key,
                    label: APPROVER_MODE.REMOVE?.label,
                    disabled: checkingRemoveApprovers,
                    onClick: () => {
                      if (
                        !includes(
                          currentTenant?.tenant?.addOn,
                          ADDONS?.MULTI_LEVEL_APPROVAL,
                        )
                      ) {
                        setFeatureKey(
                          REQUEST_FEATURE_UPGRADE_KEYS.STAGE_APPROVAL,
                        );
                        setShowUpgradeModal(true);
                      } else {
                        const queryFilter = {
                          projectEqcTypeId: Number(eqcTypeId),
                        };
                        if (isSelectAllAcrossPages) {
                          queryFilter.isAllSelected = true;
                          if (exceptIds?.length > 0) {
                            queryFilter.exceptProjectEqcTypeStageIds =
                              exceptIds;
                          }
                        } else {
                          queryFilter.projectEqcTypeStageIds =
                            selectedRows?.map((row) => row?.id) || [];
                        }
                        getStageLevelWiseUsers({
                          variables: { filter: queryFilter },
                        });
                      }
                    },
                  },
                ],
              }}
              trigger={['click']}
              getPopupContainer={(triggerNode) => triggerNode?.parentNode}
            >
              <CommonTooltip trigger="hover" title="Bulk Edit Approvers">
                <Button
                  shape="round"
                  icon={<PersonDone />}
                  className="edit-button pointer b-0"
                  onClick={(e) => e.stopPropagation()}
                />
              </CommonTooltip>
            </Dropdown>
            <CommonTooltip trigger="hover" title="Bulk Edit Settings">
              <Button
                shape="round"
                icon={<SettingOutlined />}
                className="edit-button pointer b-0 ml-5"
                onClick={() => {
                  setShowBulkEditSettingsDrawer(true);
                }}
              />
            </CommonTooltip>
            <CommonTooltip trigger="hover" title="Bulk Delete">
              <Button
                className="delete-button pointer b-0 ml-5 mr-5"
                shape="round"
                icon={<DeleteButton />}
                onClick={handleBulkDelete}
              />
            </CommonTooltip>
          </CommonSelectionBar>
        </Portal>
      )}
      {eqcTypeData?.project?.isActive && isEditable && (
        <CanPerform
          action={ALLOWED_ACTION_KEYS.ADD_PROJECT_STAGE}
          type={ALLOWED_ACTION_TYPE.BOTH}
        >
          <Portal portalId="eqc-details-btn">
            <Portal portalId="add-btn">
              <CommonTooltip title={addButtonTooltip}>
                <Button
                  shape="round"
                  type="primary"
                  id="add-btn"
                  icon={<AddButton />}
                  disabled={
                    !eqcTypeData?.project?.isActive ||
                    isStageItemSelectionActive ||
                    isAnyRowSelected
                  }
                  className="add-button b-0"
                  onClick={() => {
                    if (
                      activeStageCount <
                      currentTenant?.tenant?.featureConfig?.stageMaxLimit
                    ) {
                      handleAddEditUser();
                    } else {
                      setFeatureKey(
                        REQUEST_FEATURE_UPGRADE_KEYS.STAGE_MAX_LIMIT,
                      );
                      setShowUpgradeModal(true);
                    }
                  }}
                >
                  Add
                </Button>
              </CommonTooltip>
            </Portal>
          </Portal>
        </CanPerform>
      )}
      <div className="pointer">
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={DraggableBodyRow}
          sensors={sensors}
        >
          <SortableContext
            items={dataSource?.map((i) => i?.id) ?? []}
            strategy={verticalListSortingStrategy}
          >
            <CommonTable
              rowClassName={(record) => {
                return getRowClassName(record);
              }}
              onRow={(record) => {
                return {
                  onClick: () => {
                    if (isAnyRowSelected) return;
                    if (record?.id) {
                      setTitle(record?.name);
                      setStageId(record?.id);
                    }
                  },
                };
              }}
              components={{ body: { row: Rows } }}
              columns={columns}
              dataSource={dataSource || []}
              onChange={handleTableChange}
              paginationConfig={paginationProp}
              rowKey="id"
              loading={loading || reorderLoading}
              className="checklist-row"
              rowSelection={getRowSelection()}
            />
          </SortableContext>
        </DndContext>
      </div>
      {open && !isDesktopViewport && (
        <ApproversDrawer open={open} setOpen={setOpen} height={350}>
          <div onClick={(e) => e.stopPropagation()}>
            <Descriptions
              column={3}
              layout="vertical"
              colon={false}
              className="inner"
            >
              {getLevelWiseApprovers(drawerData)}
            </Descriptions>
          </div>
        </ApproversDrawer>
      )}
      {showUpgradeModal && (
        <UpgradeModal
          showModal={showUpgradeModal}
          setShowModal={setShowUpgradeModal}
          projectId={projectId}
          featureKey={featureKey}
          isAlreadyRequested={
            currentTenant?.tenant?.featureApprovalRequestConfig?.[
              featureKey === REQUEST_FEATURE_UPGRADE_KEYS.STAGE_APPROVAL
                ? 'stageApprovalRequestSent'
                : 'stageMaxLimitRequestSent'
            ]
          }
        />
      )}
      {showBulkEditSettingsDrawer && (
        <BulkEditSettingsDrawer
          showDrawer={showBulkEditSettingsDrawer}
          setShowDrawer={setShowBulkEditSettingsDrawer}
          selectedRows={selectedRows}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          exceptIds={exceptIds}
          paginationProp={paginationProp}
          getBulkProjectStageJobStatus={getBulkProjectStageJobStatus}
          startJobTracking={(jobId) =>
            startJobTracking(jobId, BULK_ACTION.EDIT_SETTINGS)
          }
          onSuccess={() => {
            handleClearBulkSelection();
          }}
          projectEqcTypeId={eqcTypeId}
          eqcTypeData={eqcTypeData}
          projectId={projectId}
        />
      )}
      {showBulkAssignApproversDrawer && (
        <BulkAssignApproversDrawer
          showDrawer={showBulkAssignApproversDrawer}
          setShowDrawer={setShowBulkAssignApproversDrawer}
          selectedRows={selectedRows}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          exceptIds={exceptIds}
          paginationProp={paginationProp}
          rfiStageCount={rfiStageCount}
          getBulkProjectStageJobStatus={getBulkProjectStageJobStatus}
          startJobTracking={(jobId) =>
            startJobTracking(jobId, BULK_ACTION.EDIT_APPROVERS)
          }
          onSuccess={() => {
            handleClearBulkSelection();
          }}
          projectEqcTypeId={eqcTypeId}
          projectId={projectId}
        />
      )}
      {showBulkRemoveApproversDrawer && (
        <BulkRemoveApproversDrawer
          showDrawer={showBulkRemoveApproversDrawer}
          setShowDrawer={(val) => {
            setShowBulkRemoveApproversDrawer(val);
            if (!val) setPrefetchedStageData(null);
          }}
          selectedRows={selectedRows}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          exceptIds={exceptIds}
          paginationProp={paginationProp}
          prefetchedStageData={prefetchedStageData}
          getBulkProjectStageJobStatus={getBulkProjectStageJobStatus}
          startJobTracking={(jobId) =>
            startJobTracking(jobId, BULK_ACTION.EDIT_APPROVERS)
          }
          onSuccess={() => {
            handleClearBulkSelection();
          }}
          projectEqcTypeId={eqcTypeId}
          projectId={projectId}
        />
      )}
      {showBulkDeleteModal && (
        <CommonBulkDeleteModal
          showModal={showBulkDeleteModal}
          setShowModal={setShowBulkDeleteModal}
          selectedCount={
            isSelectAllAcrossPages
              ? (activeStageCount || 0) - (exceptIds?.length || 0)
              : selectedRows?.length || 0
          }
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          totalCount={paginationProp?.total || 0}
          onConfirm={handleBulkDeleteConfirm}
          loading={bulkDeleteLoading}
          entityName="stage"
          entityNamePlural="stages"
        />
      )}
      {showEmailModal && (
        <AddEmailModal
          showModal={showEmailModal}
          setShowModal={setShowEmailModal}
          usersWithoutEmail={approversWithoutEmail}
        />
      )}
    </div>
  );
};

export default StageListTable;
